import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import {
  EnvelopeIcon,
  TrashIcon,
  ArrowPathIcon,
  UserPlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

const ROLES = ['admin', 'editor', 'viewer', 'scorer', 'user'] as const;

function UserActionSheet({
  user,
  currentUserId,
  onClose,
  onToggleActive,
  onChangeRole,
  onDelete,
}: {
  user: AdminUser;
  currentUserId: string | null;
  onClose: () => void;
  onToggleActive: (id: string, current: boolean) => Promise<void>;
  onChangeRole: (id: string, role: string) => Promise<void>;
  onDelete: (id: string, email: string) => Promise<void>;
}) {
  const isSelf = user.id === currentUserId;
  const [loading, setLoading] = useState(false);

  const wrap = async (fn: () => Promise<void>) => {
    setLoading(true);
    await fn();
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-white rounded-t-2xl shadow-xl pb-8"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
          <div>
            <p className="font-semibold text-golf-gray text-sm">{user.email}</p>
            {isSelf && <p className="text-xs text-gray-400 mt-0.5">You</p>}
          </div>
          <button onClick={onClose} className="p-1 text-gray-400">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-4 space-y-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Change Role</p>
          <div className="grid grid-cols-2 gap-2">
            {ROLES.map(r => (
              <button
                key={r}
                disabled={isSelf || loading || user.role === r}
                onClick={() => wrap(() => onChangeRole(user.id, r))}
                className={`py-2 rounded-xl text-sm font-medium transition-colors capitalize
                  ${user.role === r
                    ? 'bg-golf-orange text-white'
                    : 'bg-gray-100 text-gray-600 active:bg-gray-200 disabled:opacity-40'}`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 pt-5 space-y-2">
          {!isSelf && (
            <button
              disabled={loading}
              onClick={() => wrap(() => onToggleActive(user.id, user.is_active))}
              className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors
                ${user.is_active
                  ? 'bg-amber-50 text-amber-600 active:bg-amber-100'
                  : 'bg-green-50 text-green-600 active:bg-green-100'}`}
            >
              {user.is_active ? 'Deactivate User' : 'Activate User'}
            </button>
          )}
          {!isSelf && (
            <button
              disabled={loading}
              onClick={() => wrap(() => onDelete(user.id, user.email))}
              className="w-full py-3 rounded-xl text-sm font-semibold bg-red-50 text-red-600 active:bg-red-100 transition-colors"
            >
              Remove User
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MobileUserManagement() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [actionUser, setActionUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
  }, []);

  const fetchCurrentUser = async () => {
    const { data } = await supabase.auth.getUser();
    if (data.user) setCurrentUserId(data.user.id);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('auth_users')
        .select('id, email, full_name, role, is_active, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setUsers(data || []);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const response = await fetch(`${supabaseUrl}/functions/v1/invite-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || anonKey}`,
          'Apikey': anonKey,
        },
        body: JSON.stringify({ email: inviteEmail, redirectTo: `${window.location.origin}/dashboard` }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Failed to send invite');
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleToggleActive = async (userId: string, current: boolean) => {
    const { error } = await supabase
      .from('auth_users')
      .update({ is_active: !current })
      .eq('id', userId);
    if (error) { toast.error('Failed to update user'); return; }
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !current } : u));
    toast.success(current ? 'User deactivated' : 'User activated');
  };

  const handleChangeRole = async (userId: string, role: string) => {
    const { error } = await supabase
      .from('auth_users')
      .update({ role })
      .eq('id', userId);
    if (error) { toast.error('Failed to update role'); return; }
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
    toast.success('Role updated');
  };

  const handleDelete = async (userId: string, email: string) => {
    if (!window.confirm(`Remove ${email}? This cannot be undone.`)) return;
    const { error } = await supabase.from('auth_users').delete().eq('id', userId);
    if (error) { toast.error('Failed to remove user'); return; }
    setUsers(prev => prev.filter(u => u.id !== userId));
    toast.success('User removed');
  };

  const roleBadge = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-golf-orange/10 text-golf-orange';
      case 'editor': return 'bg-blue-100 text-blue-700';
      case 'viewer': return 'bg-teal-100 text-teal-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-3">
            <UserPlusIcon className="w-5 h-5 text-golf-orange" />
            <p className="text-sm font-semibold text-golf-gray">Invite Admin User</p>
          </div>
          <form onSubmit={handleInvite} className="space-y-3">
            <input
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="admin@example.com"
              required
              disabled={inviteLoading}
              className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange"
            />
            <button
              type="submit"
              disabled={inviteLoading}
              className="w-full py-2.5 rounded-xl bg-golf-orange text-white text-sm font-semibold disabled:opacity-50 active:bg-golf-orange/90 transition-colors"
            >
              {inviteLoading ? 'Sending...' : 'Send Invite'}
            </button>
          </form>
          <p className="text-xs text-gray-400 mt-2">
            The user will receive a password setup email to access the admin dashboard.
          </p>
        </div>

        <div className="flex items-center justify-between px-1">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Users</p>
          <button
            onClick={fetchUsers}
            className="flex items-center gap-1.5 text-xs text-gray-400 active:text-gray-600"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-8 h-8 rounded-full border-4 border-golf-orange border-t-transparent animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
            <ShieldCheckIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No admin users found.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {users.map(user => (
              <div
                key={user.id}
                className={`bg-white rounded-2xl border border-gray-100 p-4 ${!user.is_active ? 'opacity-60' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
                    <EnvelopeIcon className="w-4 h-4 text-golf-orange" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {user.full_name && (
                      <p className="text-sm font-semibold text-golf-gray truncate">{user.full_name}</p>
                    )}
                    <p className={`text-sm truncate ${user.full_name ? 'text-gray-500' : 'font-semibold text-golf-gray'}`}>
                      {user.email}
                    </p>
                    {user.id === currentUserId && (
                      <p className="text-xs text-gray-400">You</p>
                    )}
                  </div>
                  <button
                    onClick={() => setActionUser(user)}
                    className="p-1.5 text-gray-400 active:text-gray-600 flex-shrink-0"
                  >
                    <EllipsisVerticalIcon className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${roleBadge(user.role)} capitalize`}>
                    {user.role}
                  </span>
                  {user.is_active ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                      <CheckCircleIcon className="w-3 h-3" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                      <XCircleIcon className="w-3 h-3" /> Inactive
                    </span>
                  )}
                  {user.id !== currentUserId && (
                    <button
                      onClick={() => handleDelete(user.id, user.email)}
                      className="ml-auto p-1.5 text-red-400 active:text-red-600"
                      title="Remove user"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {actionUser && (
        <UserActionSheet
          user={actionUser}
          currentUserId={currentUserId}
          onClose={() => setActionUser(null)}
          onToggleActive={handleToggleActive}
          onChangeRole={handleChangeRole}
          onDelete={handleDelete}
        />
      )}
    </>
  );
}
